/**
 * Grafana Faro monitoring integration for DataTru frontend.
 *
 * Provides real user monitoring (RUM) with user context correlation
 * following Django Two Scoops patterns and DataTru conventions.
 */

import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

const ANONYMOUS_ID_STORAGE_KEY = 'datatru:faro:anonymous_id';
const ANONYMOUS_ID_COOKIE_NAME = 'datatru_faro_anon_id';
const ANONYMOUS_ID_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year
const DEFAULT_SERVICE_NAME = 'datatru-frontend';
const DEFAULT_LEVEL = 'info';
const KNOWN_LEVELS = new Set([
  'trace',
  'debug',
  'info',
  'warn',
  'error',
  'critical',
]);

const LEVEL_SYNONYMS = {
  warning: 'warn',
  warn: 'warn',
  verbose: 'debug',
  fatal: 'critical',
  critical: 'critical',
  err: 'error',
  severe: 'error',
};

function normalizeLevel(value, fallback = DEFAULT_LEVEL) {
  if (typeof value === 'string' && value.trim()) {
    const normalized = value.trim().toLowerCase();
    if (KNOWN_LEVELS.has(normalized)) {
      return normalized;
    }
    if (LEVEL_SYNONYMS[normalized]) {
      return LEVEL_SYNONYMS[normalized];
    }
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 50) {
      return 'error';
    }
    if (value >= 40) {
      return 'warn';
    }
    if (value >= 30) {
      return 'info';
    }
    if (value >= 20) {
      return 'debug';
    }
    return 'trace';
  }

  return fallback;
}

function safeLocalStorageGet(key) {
  try {
    return window.localStorage ? window.localStorage.getItem(key) : null;
  } catch (error) {
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  try {
    if (!window.localStorage) {
      return false;
    }
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
}

function readCookie(name) {
  if (typeof document === 'undefined') {
    return null;
  }
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const cookie of cookies) {
    const [cookieName, ...cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue.join('='));
    }
  }
  return null;
}

function writeCookie(name, value, maxAgeSeconds) {
  if (typeof document === 'undefined') {
    return;
  }
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    'SameSite=Lax',
  ];
  if (Number.isFinite(maxAgeSeconds)) {
    parts.push(`Max-Age=${maxAgeSeconds}`);
  }
  document.cookie = parts.join('; ');
}

function generateAnonymousId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return [
    'anon',
    Date.now().toString(36),
    Math.random().toString(36).slice(2),
  ].join('-');
}

function getOrCreateAnonymousId(fallbackId = null) {
  if (typeof window === 'undefined') {
    return fallbackId || 'anonymous';
  }

  const namespace = (window.DataTru = window.DataTru || {});
  if (namespace.anonymousId) {
    return namespace.anonymousId;
  }

  let anonymousId = safeLocalStorageGet(ANONYMOUS_ID_STORAGE_KEY);
  if (!anonymousId) {
    anonymousId = readCookie(ANONYMOUS_ID_COOKIE_NAME);
  }
  if (!anonymousId) {
    anonymousId = fallbackId || generateAnonymousId();
    const stored = safeLocalStorageSet(ANONYMOUS_ID_STORAGE_KEY, anonymousId);
    if (!stored) {
      // Storage might be disabled (Safari private mode). Cookie keeps a durable identifier.
      writeCookie(
        ANONYMOUS_ID_COOKIE_NAME,
        anonymousId,
        ANONYMOUS_ID_MAX_AGE_SECONDS,
      );
    }
  } else {
    // Refresh persistence so it stays valid
    safeLocalStorageSet(ANONYMOUS_ID_STORAGE_KEY, anonymousId);
  }
  writeCookie(
    ANONYMOUS_ID_COOKIE_NAME,
    anonymousId,
    ANONYMOUS_ID_MAX_AGE_SECONDS,
  );

  namespace.anonymousId = anonymousId;
  return anonymousId;
}

function pruneEmpty(values = {}) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => {
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === 'string' && value.trim() === '') {
        return false;
      }
      return true;
    }),
  );
}

function prepareMeta(meta, { config, sessionContext, userContext }) {
  const prepared = { ...(meta || {}) };

  if (config.requestId) {
    prepared.requestId = config.requestId;
  }

  if (userContext.organizationId) {
    prepared.organizationId = userContext.organizationId;
  }

  if (userContext.id) {
    prepared.userId = userContext.id;
  }

  if (userContext.authState) {
    prepared.userAuthState = userContext.authState;
  }

  if (userContext.anonymousId) {
    prepared.anonymousId = userContext.anonymousId;
  }

  if (userContext.authenticatedId) {
    prepared.authenticatedUserId = userContext.authenticatedId;
  }

  if (userContext.id) {
    prepared.uniqueUser = userContext.id;
  }

  if (sessionContext.id) {
    prepared.sessionId = sessionContext.id;
  }

  if (sessionContext.state) {
    prepared.sessionState = sessionContext.state;
  }

  if (
    Array.isArray(sessionContext.behaviors) &&
    sessionContext.behaviors.length
  ) {
    prepared.sessionBehaviors = sessionContext.behaviors;
  }

  if (!prepared.service_name) {
    prepared.service_name = DEFAULT_SERVICE_NAME;
  }

  if (!prepared.environment && config.environment) {
    prepared.environment = config.environment;
  }

  return prepared;
}

function extractLevelCandidates(item) {
  const payload = item?.payload || {};
  const payloadContext = payload?.context || {};
  const attributes =
    payload?.attributes || payload?.eventAttributes || payloadContext || {};

  return [
    item?.meta?.level,
    attributes.level,
    attributes.severity,
    attributes.logLevel,
    attributes.severityText,
    payload.level,
    payload.severity,
    payload.severityText,
    payloadContext.level,
    payloadContext.severity,
    payloadContext.severityText,
  ];
}

function determineLevel(item) {
  const candidates = extractLevelCandidates(item);
  const fallback = item?.type === 'exception' ? 'error' : DEFAULT_LEVEL;
  const resolvedCandidate = candidates.find((candidate) => {
    const normalized = normalizeLevel(candidate, null);
    return normalized !== null && normalized !== undefined;
  });

  const normalized = normalizeLevel(resolvedCandidate, fallback);
  return normalized || fallback;
}

function ensureEventTimestamp(attributes) {
  if (!attributes.timestamp) {
    attributes.timestamp = new Date().toISOString();
  }
}

export function prepareEventAttributes(
  attributes = {},
  { defaultLevel = DEFAULT_LEVEL, eventType } = {},
) {
  const prepared = { ...(attributes || {}) };
  const levelSource =
    prepared.level ||
    prepared.severity ||
    prepared.logLevel ||
    prepared.severityText;
  const level = normalizeLevel(levelSource, defaultLevel);

  prepared.level = level;
  prepared.severity = level;
  prepared.logLevel = level;

  if (eventType && !prepared.eventType) {
    prepared.eventType = eventType;
  }

  ensureEventTimestamp(prepared);

  return prepared;
}

// Seed Core Web Vital events so dashboards show activity even on quiet local loads.
function installWebVitalSeedEvent(faro, { log, featureFlags }) {
  if (
    !faro ||
    !faro.api ||
    typeof faro.api.pushEvent !== 'function' ||
    (featureFlags && featureFlags.performanceMonitoring === false)
  ) {
    return;
  }

  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof window.performance === 'undefined'
  ) {
    return;
  }

  const namespace = (window.DataTru = window.DataTru || {});
  if (namespace.webVitalSeeded) {
    return;
  }
  namespace.webVitalSeeded = true;

  const isFiniteNumber = (value) =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0;

  const sendMetric = (metric, value, extra = {}) => {
    if (!isFiniteNumber(value)) {
      return;
    }

    try {
      const attributes = prepareEventAttributes(
        {
          metric,
          value: Math.round(value),
          unit: extra.unit || 'ms',
          source: extra.source || 'performance_timing',
          seeded: true,
          path: window.location.pathname,
          ...extra,
        },
        {
          defaultLevel: 'info',
          eventType: 'web_vital',
        },
      );

      faro.api.pushEvent('web_vital', attributes);
      log.log('[Faro] Seeded web vital metric', metric, attributes.value);
    } catch (error) {
      log.warn('[Faro] Failed to seed web vital metric', metric, error);
    }
  };

  const collectFromTiming = () => {
    const timing = window.performance.timing;
    if (!timing) {
      return;
    }

    const navigationStart = timing.navigationStart || 0;
    if (!navigationStart) {
      return;
    }

    const domContentLoaded = timing.domContentLoadedEventEnd - navigationStart;
    sendMetric('dom_content_loaded', domContentLoaded, {
      source: 'performance_timing',
    });

    const loadComplete = timing.loadEventEnd - navigationStart;
    sendMetric('page_load_complete', loadComplete, {
      source: 'performance_timing',
    });
  };

  const collectPaintTimings = () => {
    const getEntriesByType = window.performance.getEntriesByType;
    if (typeof getEntriesByType !== 'function') {
      return;
    }

    const paintEntries =
      getEntriesByType.call(window.performance, 'paint') || [];
    paintEntries.forEach((entry) => {
      const metricName = entry?.name?.replace(/-/g, '_');
      const value = entry?.startTime;
      if (!metricName) {
        return;
      }

      sendMetric(metricName, value, {
        unit: 'ms',
        source: 'performance_paint',
      });
    });
  };

  const scheduleCollection = () => {
    try {
      collectFromTiming();
      collectPaintTimings();
    } catch (error) {
      log.warn('[Faro] Web vital seeding failed', error);
    }
  };

  if (document.readyState === 'complete') {
    setTimeout(scheduleCollection, 0);
  } else {
    window.addEventListener(
      'load',
      () => {
        setTimeout(scheduleCollection, 0);
      },
      { once: true },
    );
  }
}

/**
 * Initialize Grafana Faro with user context and configuration.
 *
 * @param {Object} config - Faro configuration object
 * @param {string} config.url - Faro collector URL
 * @param {string} config.environment - Environment (development, production)
 * @param {Object} config.user - User context information
 * @param {Object} config.session - Session context metadata
 * @param {string} config.requestId - Request ID for correlation
 * @param {Object} config.features - Feature flags from the backend
 */
export function initializeFaroMonitoring(config) {
  const debugLoggingEnabled = config.debugLogging === true;
  const log = {
    log: (...args) => {
      if (debugLoggingEnabled) {
        console.log(...args);
      }
    },
    warn: (...args) => {
      if (debugLoggingEnabled) {
        console.warn(...args);
      }
    },
    error: (...args) => {
      if (debugLoggingEnabled) {
        console.error(...args);
      }
    },
  };

  // Validate required configuration
  if (!config.url) {
    log.warn('[Faro] No collector URL provided, skipping initialization');
    return null;
  }

  const featureFlags = config.features || {};
  const sessionConfig = config.session || {};
  const sessionId = sessionConfig.id || config.sessionId || null;
  const sessionContext = {
    ...sessionConfig,
    id: sessionId,
  };
  const captureConsole = featureFlags.captureConsole === true;

  const anonymousId = getOrCreateAnonymousId(
    sessionContext.frontendId ||
      sessionId ||
      config.sessionId ||
      (config.user && config.user.id) ||
      null,
  );
  const backendUser = config.user || {};
  const organizationId = backendUser.organizationId || null;
  const trackingId = backendUser.trackingId || null;
  const isAuthenticated = backendUser.isAuthenticated === true;
  const derivedAuthState =
    sessionContext.state || (isAuthenticated ? 'authenticated' : 'anonymous');
  sessionContext.state = derivedAuthState;
  const authState = derivedAuthState;
  const userType = isAuthenticated ? 'authenticated' : 'anonymous';
  const effectiveUserId =
    (isAuthenticated && trackingId) ||
    anonymousId ||
    sessionId ||
    backendUser.id ||
    'anonymous';

  const userAttributes = pruneEmpty({
    auth_state: authState,
    user_type: userType,
    account_id: organizationId,
    anonymous_id: anonymousId,
    authenticated_id: trackingId,
    unique_user: effectiveUserId,
  });

  const faroUser = pruneEmpty({
    id: effectiveUserId,
    email: null,
    attributes: Object.keys(userAttributes).length ? userAttributes : undefined,
  });

  const userContext = {
    ...faroUser,
    organizationId,
    isAuthenticated,
    sessionId,
    authState,
    anonymousId,
    authenticatedId: trackingId,
  };

  if (typeof window !== 'undefined') {
    window.DataTru = window.DataTru || {};
    if (typeof window.DataTru.debugLogging === 'undefined') {
      window.DataTru.debugLogging = debugLoggingEnabled;
    }
    window.DataTru.debugLog = (level, ...args) => {
      const logger = log[level] || log.log;
      logger(...args);
    };
    if (anonymousId) {
      window.DataTru.anonymousId = anonymousId;
    }
    window.DataTru.userAuthState = authState;
    if (trackingId) {
      window.DataTru.userTrackingId = trackingId;
    }
    if (sessionId) {
      window.DataTru.sessionId = sessionId;
    }
    window.DataTru.userContext = userContext;
  }

  // Configure Faro initialization
  const faroConfig = {
    url: config.url,
    app: {
      name: 'datatru-frontend',
      version: '1.0.0',
      environment: config.environment || 'development',
    },

    // User identification for session correlation
    user: faroUser,

    // Session and request correlation
    sessionTracking: {
      enabled: featureFlags.sessionTracking !== false,
      persistent: true,
    },

    // Include default web instrumentations
    instrumentations: [
      ...getWebInstrumentations({
        // Capture page load and navigation performance
        captureConsole,
        captureErrors: featureFlags.errorTracking !== false,

        // Web vitals for Core Web Vitals tracking
        webVitals: {
          enabled: featureFlags.performanceMonitoring !== false,
        },

        // Session tracking
        session: {
          enabled: featureFlags.sessionTracking !== false,
          trackAttributes: ['user-agent', 'viewport', 'language'],
        },
      }),

      // OpenTelemetry tracing for correlation with backend
      new TracingInstrumentation({
        // Correlate frontend traces with backend request traces
        propagateTraceHeaderCorsUrls: [
          // Add your API endpoints for trace correlation
          new RegExp(`${window.location.origin}/api/.*`),
          new RegExp(`${window.location.origin}/billing/.*`),
          new RegExp(`${window.location.origin}/dashboard/.*`),
        ],
      }),
    ],

    // Batch configuration for performance
    batching: {
      enabled: true,
      sendTimeout: 5000, // 5 seconds
      itemLimit: 50,
    },

    // Privacy and data handling
    beforeSend: (item) => {
      const meta = prepareMeta(item.meta, {
        config,
        sessionContext,
        userContext,
      });

      meta.level = determineLevel({
        ...item,
        meta,
      });
      meta.severity = meta.level;
      meta.logLevel = meta.level;

      const payload = item.payload || {};
      const attributes = {
        ...(payload.attributes ||
          payload.eventAttributes ||
          payload.context ||
          {}),
      };

      attributes.level = meta.level;
      attributes.severity = meta.level;
      attributes.logLevel = meta.level;
      if (!attributes.auth_state && authState) {
        attributes.auth_state = authState;
      }
      if (!attributes.user_type && userType) {
        attributes.user_type = userType;
      }
      if (!attributes.user_id && userContext.id) {
        attributes.user_id = userContext.id;
      }
      if (!attributes.session_id && sessionContext.id) {
        attributes.session_id = sessionContext.id;
      }
      if (!attributes.anonymous_id && userContext.anonymousId) {
        attributes.anonymous_id = userContext.anonymousId;
      }
      if (!attributes.authenticated_id && userContext.authenticatedId) {
        attributes.authenticated_id = userContext.authenticatedId;
      }
      if (!attributes.account_id && userContext.organizationId) {
        attributes.account_id = userContext.organizationId;
      }
      if (!attributes.unique_user && userContext.id) {
        attributes.unique_user = userContext.id;
      }
      ensureEventTimestamp(attributes);

      payload.attributes = attributes;
      item.payload = payload;
      item.meta = meta;
      return item;
    },
  };

  try {
    const faro = initializeFaro(faroConfig);

    // Add custom metadata
    faro.api.setUser(faroUser);

    // Set session attributes for better correlation
    if (sessionId) {
      const sessionPayload = {
        id: sessionId,
        state:
          sessionContext.state ||
          (userContext.isAuthenticated ? 'authenticated' : 'anonymous'),
        startedAt: sessionContext.startedAt || null,
        lastActivityAt: sessionContext.lastActivityAt || null,
        requestCount: sessionContext.requestCount || 0,
        behaviors: sessionContext.behaviors || [],
        isActive: sessionContext.isActive ?? true,
        durationSeconds: sessionContext.durationSeconds ?? null,
        idleSeconds: sessionContext.idleSeconds ?? null,
      };

      if (sessionContext.frontendId) {
        sessionPayload.frontendId = sessionContext.frontendId;
      }

      if (sessionContext.authenticatedAt) {
        sessionPayload.authenticatedAt = sessionContext.authenticatedAt;
      }

      if (sessionContext.expiresAt) {
        sessionPayload.expiresAt = sessionContext.expiresAt;
      }

      const sessionAttributes = pruneEmpty({
        auth_state: authState,
        user_type: userType,
        user_id: userContext.id,
        anonymous_id: userContext.anonymousId,
        authenticated_id: userContext.authenticatedId,
        session_id: sessionId,
        account_id: organizationId,
        unique_user: userContext.id,
      });

      if (Object.keys(sessionAttributes).length) {
        sessionPayload.attributes = sessionAttributes;
      }

      faro.api.setSession(sessionPayload);

      if (sessionPayload.isActive) {
        faro.api.pushEvent(
          'session_active',
          prepareEventAttributes(
            pruneEmpty({
              state: sessionPayload.state,
              behaviors: sessionPayload.behaviors,
              requestCount: sessionPayload.requestCount,
              durationSeconds: sessionPayload.durationSeconds,
              idleSeconds: sessionPayload.idleSeconds,
              auth_state: authState,
              user_type: userType,
              user_id: userContext.id,
              anonymous_id: userContext.anonymousId,
              authenticated_id: userContext.authenticatedId,
              session_id: sessionId,
              account_id: organizationId,
              unique_user: userContext.id,
            }),
            {
              defaultLevel: 'info',
              eventType: 'session',
            },
          ),
        );
      }
    }

    log.log('[Faro] Initialized successfully', {
      userId: userContext.id,
      environment: config.environment,
      authenticated: userContext.isAuthenticated,
      sessionState: sessionContext.state,
    });

    installWebVitalSeedEvent(faro, { log, featureFlags });

    return faro;
  } catch (error) {
    log.error('[Faro] Initialization failed:', error);
    return null;
  }
}

/**
 * Get the Faro instance from the DataTru namespace.
 *
 * @returns {Object|null} - Faro instance or null if not available
 */
function getFaroInstance() {
  return window.DataTru?.faro || window.faroInstance || window.faro || null;
}

/**
 * Track custom business events with user context.
 *
 * @param {string} eventName - Name of the event
 * @param {Object} attributes - Event attributes
 */
export function trackBusinessEvent(eventName, attributes = {}) {
  const faroInstance = getFaroInstance();
  if (faroInstance) {
    faroInstance.api.pushEvent(
      eventName,
      prepareEventAttributes(attributes, {
        defaultLevel: 'info',
        eventType: 'business',
      }),
    );
  } else {
    debugLog(
      'warn',
      '[Faro] No instance available for tracking event:',
      eventName,
    );
  }
}

/**
 * Track user authentication events.
 *
 * @param {string} action - Authentication action (login, logout, signup)
 * @param {Object} context - Additional context
 */
export function trackAuthEvent(action, context = {}) {
  trackBusinessEvent('auth_event', {
    action,
    ...context,
  });
}

/**
 * Track subscription and billing events.
 *
 * @param {string} action - Billing action
 * @param {Object} context - Subscription context
 */
export function trackBillingEvent(action, context = {}) {
  trackBusinessEvent('billing_event', {
    action,
    ...context,
  });
}

/**
 * Track workspace and resource usage.
 *
 * @param {string} action - Workspace action
 * @param {Object} context - Workspace context
 */
export function trackWorkspaceEvent(action, context = {}) {
  trackBusinessEvent('workspace_event', {
    action,
    ...context,
  });
}

/**
 * Track session lifecycle and behavior transitions.
 *
 * @param {string} eventName - Session event name
 * @param {Object} attributes - Session attributes
 */
export function trackSessionEvent(eventName, attributes = {}) {
  const faroInstance = getFaroInstance();
  if (faroInstance) {
    faroInstance.api.pushEvent(
      eventName,
      prepareEventAttributes(attributes, {
        defaultLevel: 'info',
        eventType: 'session',
      }),
    );
  } else {
    debugLog(
      'warn',
      '[Faro] No instance available for tracking session event:',
      eventName,
    );
  }
}

/**
 * Update user context when authentication state changes.
 *
 * @param {Object} userContext - Updated user context
 */
export function updateUserContext(userContext = {}) {
  const faroInstance = getFaroInstance();
  if (!faroInstance) {
    debugLog('warn', '[Faro] No instance available for updating user context');
    return;
  }

  const namespace =
    typeof window !== 'undefined'
      ? (window.DataTru = window.DataTru || {})
      : {};
  const isAuthenticated = Boolean(userContext.isAuthenticated);
  const trackingId =
    userContext.trackingId ||
    userContext.authenticatedId ||
    (isAuthenticated ? userContext.id : null) ||
    namespace.userTrackingId ||
    null;
  const anonymousId =
    userContext.anonymousId ||
    namespace.anonymousId ||
    getOrCreateAnonymousId(namespace.sessionId);
  const authState =
    userContext.authState || (isAuthenticated ? 'authenticated' : 'anonymous');
  const organizationId =
    userContext.organizationId || namespace.userContext?.organizationId || null;
  const effectiveUserId =
    (isAuthenticated && trackingId) ||
    anonymousId ||
    namespace.userContext?.id ||
    'anonymous';
  const userType = isAuthenticated ? 'authenticated' : 'anonymous';

  const userAttributes = pruneEmpty({
    auth_state: authState,
    user_type: userType,
    account_id: organizationId,
    anonymous_id: anonymousId,
    authenticated_id: trackingId,
    unique_user: effectiveUserId,
  });

  const faroUser = pruneEmpty({
    id: effectiveUserId,
    email: null,
    attributes: Object.keys(userAttributes).length ? userAttributes : undefined,
  });

  faroInstance.api.setUser(faroUser);

  namespace.userContext = {
    ...namespace.userContext,
    ...faroUser,
    organizationId,
    isAuthenticated,
    authState,
    anonymousId,
    authenticatedId: trackingId,
    uniqueUser: effectiveUserId,
  };
  if (anonymousId) {
    namespace.anonymousId = anonymousId;
  }
  if (trackingId) {
    namespace.userTrackingId = trackingId;
  }
  namespace.userAuthState = authState;

  const sessionId = namespace.sessionId || userContext.sessionId || null;
  if (sessionId) {
    const sessionAttributes = pruneEmpty({
      auth_state: authState,
      user_type: userType,
      user_id: faroUser.id,
      anonymous_id: anonymousId,
      authenticated_id: trackingId,
      session_id: sessionId,
      account_id: organizationId,
      unique_user: faroUser.id,
    });

    const sessionPayload = pruneEmpty({
      id: sessionId,
      state: authState,
      attributes: Object.keys(sessionAttributes).length
        ? sessionAttributes
        : undefined,
    });

    faroInstance.api.setSession(sessionPayload);
    faroInstance.api.pushEvent(
      'session_active',
      prepareEventAttributes(
        pruneEmpty({
          auth_state: authState,
          user_type: userType,
          user_id: faroUser.id,
          anonymous_id: anonymousId,
          authenticated_id: trackingId,
          session_id: sessionId,
          account_id: organizationId,
          unique_user: faroUser.id,
        }),
        {
          defaultLevel: 'info',
          eventType: 'session',
        },
      ),
    );
  }
}

// Export tracking functions for global use
export default {
  initializeFaroMonitoring,
  trackBusinessEvent,
  trackAuthEvent,
  trackBillingEvent,
  trackWorkspaceEvent,
  trackSessionEvent,
  prepareEventAttributes,
  updateUserContext,
};

function debugLog(level, ...args) {
  if (window.DataTru?.debugLogging) {
    const logger = console[level] || console.log;
    logger(...args);
  }
}
